import "core-js/modules/web.dom-collections.iterator.js";
import React, { useRef, useMemo, useEffect, useState, useCallback } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import Badge from "@mui/material/Badge";
import FilterListIcon from "@pixel/assets/icons/FilterList";
import SearchIcon from "@pixel/assets/icons/Search";
import ClearInputIcon from "@pixel/assets/icons/ClearInput";
import Paper from "../Paper";
import Box from "../Box";
import Button from "../Button";
import IconButton from "../IconButton";
import ErrorBoundary from "../ErrorBoundary";
// eslint-disable-next-line import/no-cycle
import ListHeaderCoordinator from "../ListHeaderCoordinator";
import { TextField } from "../TextField/TextField";
// eslint-disable-next-line import/no-cycle
import { isLayoutVariantCompact } from "../ListBase";
import { useTheme } from "../styles";
import useDebounce from "../utils/useDebounce";
import ListSort from "../ListSort";
import { ListSearchFiltersDrawer } from "../ListSearchFilters";

// eslint-disable-next-line import/no-cycle
import ListAppliedFilterChip from "../ListAppliedFilterChip";
;
export default function ListHeader(_ref) {
  let {
    layoutVariant = "standard",
    showSearch = true,
    labelSearch = "Search",
    labelResults = "Results",
    labelSearchAria,
    results,
    appliedSearchQuery,
    viewContexts,
    sortOrders,
    labelSortBy,
    appliedSortOrder,
    selectedViewContext,
    onChangeSelectedViewContext,
    onSearchQueryChanged,
    onSortChanged,
    searchFilters,
    appliedSearchFilters,
    color = "info",
    onSearchFilterApplied,
    selectedIds,
    allSelected,
    onSelectAllChange,
    selectionContext,
    SelectedActionGroupProps,
    GlobalActionGroupProps,
    selectionType,
    labelFilters = "Filters",
    labelClose = "Close",
    labelClear = "Clear",
    labelApplyFilters = "Apply Filters",
    labelClearAllFilters = "Clear All",
    renderPageActions
  } = _ref;
  const theme = useTheme();
  const inputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState(appliedSearchQuery);
  const [DrawerOpen, setDrawerOpen] = useState(false);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const compact = useMemo(() => isLayoutVariantCompact(layoutVariant), [layoutVariant]);
  useEffect(() => {
    if (searchTerm !== appliedSearchQuery) {
      setSearchTerm(appliedSearchQuery);
    }
  }, [appliedSearchQuery]);
  useEffect(() => {
    // note: useEffect sideEffect: check for diff with props appliedSearchQuery as we dont want to perform a search the first time this loads
    if (searchTerm !== appliedSearchQuery && typeof onSearchQueryChanged === "function") {
      onSearchQueryChanged(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);
  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };
  const handleOpenSearchFilters = (event, open) => {
    setDrawerOpen(open);
  };
  const hasMultiViewContexts = Boolean(viewContexts) && viewContexts.length > 1;
  const renderViewContexts = useMemo(() => hasMultiViewContexts && /*#__PURE__*/React.createElement(ToggleButtonGroup, {
    size: "small",
    value: selectedViewContext,
    exclusive: true,
    onChange: (e, val) => onChangeSelectedViewContext(val)
  }, viewContexts.map((viewContext, i) =>
  /*#__PURE__*/
  // eslint-disable-next-line react/no-array-index-key
  React.createElement(ToggleButton, {
    key: i,
    value: i
  }, viewContext.icon))), [hasMultiViewContexts, viewContexts, onChangeSelectedViewContext]);
  const showFilters = Boolean(searchFilters) && searchFilters.length > 0;
  const renderSearchFilters = useMemo(() => {
    if (showFilters) {
      if (compact) {
        return /*#__PURE__*/React.createElement(Button, {
          variant: "outlined",
          color: color,
          onClick: e => handleOpenSearchFilters(e, true),
          "aria-label": labelFilters,
          disableElevation: true,
          sx: {
            minWidth: 24,
            pl: 1,
            pr: 1
          }
        }, /*#__PURE__*/React.createElement(Badge, {
          badgeContent: appliedSearchFilters.length,
          color: "primary"
        }, /*#__PURE__*/React.createElement(FilterListIcon, null)));
      } else {
        return /*#__PURE__*/React.createElement(Button, {
          sx: {
            textTransform: "none",
            "&:hover": {
              boxShadow: "none"
            }
          },
          variant: "outlined",
          color: color,
          onClick: e => handleOpenSearchFilters(e, true),
          startIcon: /*#__PURE__*/React.createElement(FilterListIcon, null)
        }, labelFilters);
      }
    }
    return null;
  }, [searchFilters, appliedSearchFilters, showFilters, compact]);
  const renderSorting = useMemo(() => /*#__PURE__*/React.createElement(ListSort, {
    label: "Sort By",
    labelSortBy: labelSortBy,
    sortOrders: sortOrders,
    appliedSortOrder: appliedSortOrder,
    onSort: onSortChanged,
    color: color,
    compact: compact
  }), [sortOrders, appliedSortOrder, onSortChanged, color, compact]);
  const handleClickClearSearch = () => {
    setSearchTerm("");
    //inputRef?.current?.focus();
  };
  const handleMouseDownClearSearch = event => {
    event.preventDefault();
    handleClickClearSearch();
  };
  const handleClearAllSearchFilters = () => {
    onSearchFilterApplied([]);
  };
  const handleRemoveSearchFilter = removedFilter => {
    const newAppliedFilter = appliedSearchFilters.filter(searchFilter => searchFilter.searchFilterId !== removedFilter.appliedSearchFilter.searchFilterId);
    onSearchFilterApplied(newAppliedFilter);
  };
  const handleSearchFilterApplied = newAppliedFilter => {
    setDrawerOpen(false);
    onSearchFilterApplied(newAppliedFilter);
  };
  const hasSortOrder = sortOrders && sortOrders.length > 0;
  const [searchFocus, setSearchFocus] = useState(false);
  const handleSearchFocus = () => {
    setSearchFocus(true);
  };
  const handleSearchBlur = () => {
    setSearchFocus(false);
  };
  const handleKeyUp = e => {
    // if the user hits enter set search focus false. if they start typing again it will set it back to true except for tab or escape
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      if (searchFocus == true) {
        setSearchFocus(false);
      }
    } else if (e.key === "Escape" || e.key === "Tab") {
      setSearchFocus(false);
    } else {
      if (searchFocus == false) {
        setSearchFocus(true);
      }
    }

    // if (e.key === "Enter") {
    //   e.preventDefault();
    //   e.stopPropagation();
    //   if (searchFocus==true){
    //     setSearchFocus(false);
    //   }
    // } else if (e.key === "T") {{
    //   if (searchFocus==false){
    //     setSearchFocus(true);
    //   }
    // }
  };
  const willRenderPageActions = typeof renderPageActions === "function";
  const showActionsSelection = searchFocus == false;
  const showActionsSelectionFront = compact == false && showActionsSelection;
  const displayHideSearchFocus = searchFocus ? "none" : "flex";
  const renderListButtons = useCallback(() => {
    return /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: "flex"
      }
    }, showFilters && /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: searchFocus ? "flex" : "flex",
        paddingLeft: theme.spacing(0.5)
      }
    }, renderSearchFilters), hasSortOrder && /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: searchFocus ? "none" : "flex",
        paddingLeft: theme.spacing(0.5)
      }
    }, renderSorting), hasMultiViewContexts && /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: searchFocus ? "none" : "inline-block",
        paddingLeft: theme.spacing(0.5)
      }
    }, renderViewContexts), willRenderPageActions && /*#__PURE__*/React.createElement(ErrorBoundary, null, /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: searchFocus ? "none" : "flex",
        paddingLeft: theme.spacing(0.25)
      }
    }, renderPageActions({}))));
  }, [displayHideSearchFocus, searchFocus, renderSearchFilters, hasSortOrder, renderSorting, hasMultiViewContexts, renderViewContexts, showFilters, willRenderPageActions, renderPageActions]);
  return /*#__PURE__*/React.createElement(Paper, {
    sx: {
      padding: theme.spacing(compact == true ? 0.5 : 1),
      paddingLeft: theme.spacing(compact == true ? 1 : 2),
      paddingRight: theme.spacing(compact == true ? 1 : 2),
      overflow: "visible",
      minWidth: 0
    }
  }, /*#__PURE__*/React.createElement(Box, {
    sx: {
      overflow: "visible",
      display: "flex",
      alignItems: "center"
    }
  }, showActionsSelectionFront && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: "flex",
      flex: 0.25,
      paddingRight: theme.spacing(2)
    }
  }, /*#__PURE__*/React.createElement(ListHeaderCoordinator, {
    labelResults: labelResults,
    labelShowingTotalResults: "${total}",
    labelShowingTotalSelectedResults: "${total} Selected",
    results: results,
    selectedIds: selectedIds,
    allSelected: allSelected,
    onSelectAllChange: onSelectAllChange,
    selectionContext: selectionContext,
    SelectedActionGroupProps: SelectedActionGroupProps,
    GlobalActionGroupProps: GlobalActionGroupProps,
    selectionType: selectionType,
    compact: compact
  }))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      display: "flex",
      flex: 1,
      justifyContent: "end"
    }
  }, showSearch ? /*#__PURE__*/React.createElement(TextField, {
    id: "search",
    ref: inputRef,
    sx: {
      display: "flex",
      minWidth: compact ? 120 : 200,
      flex: 1,
      minHeight: theme.spacing(4) //because icon buttons are ~40px 5*8 based spacing and if there are no buttons we need at least 32 to make it look good
    },
    title: labelSearch,
    placeholder: !compact ? labelSearch : "" //don't show placeholder in compact mode
    ,
    "aria-label": labelSearchAria || labelSearch,
    value: searchTerm,
    onChange: handleSearchChange,
    onFocus: handleSearchFocus,
    onBlur: handleSearchBlur,
    onKeyUp: handleKeyUp,
    onClick: handleSearchFocus,
    type: "search",
    leadingIconProps: {
      icon: SearchIcon,
      ariaLabel: "Search"
    },
    InputProps: {
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "end"
      }, /*#__PURE__*/React.createElement(IconButton, {
        onClick: handleClickClearSearch,
        onMouseDown: handleMouseDownClearSearch
        //edge="end"
        ,
        size: "small",
        tabIndex: searchTerm && searchTerm.length ? 0 : -1,
        sx: {
          opacity: searchTerm && searchTerm.length ? 1 : 0,
          pointerEvents: searchTerm && searchTerm.length ? "all" : "none",
          ":focus": {
            backgroundColor: theme.palette.pixelContrast.fades.softer
          }
        }
      }, /*#__PURE__*/React.createElement(ClearInputIcon, null)))
    }
  }) : null, renderListButtons())), showFilters && /*#__PURE__*/React.createElement(ListSearchFiltersDrawer, {
    anchor: "right",
    open: DrawerOpen,
    onClose: e => handleOpenSearchFilters(e, false),
    ListSearchFiltersProps: {
      searchFilters,
      appliedSearchFilters,
      onSearchFilterApplied: handleSearchFilterApplied,
      onClose: e => handleOpenSearchFilters(e, false),
      labelFilters,
      labelClose,
      labelClear,
      labelApplyFilters,
      labelClearAllFilters
    }
  }), compact != true && appliedSearchFilters !== undefined && appliedSearchFilters.length ? /*#__PURE__*/React.createElement(Box, {
    sx: {
      paddingTop: theme.spacing(1),
      overflow: "visible"
    }
  }, /*#__PURE__*/React.createElement(ListAppliedFilterChip, {
    appliedSearchFilters: appliedSearchFilters,
    onRemoveSearchFilter: handleRemoveSearchFilter,
    onClearAllSearchFilters: handleClearAllSearchFilters,
    labelFilters: labelFilters,
    labelClearAllFilters: labelClearAllFilters
  })) : null);
}